import React, { useCallback, useEffect, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { useSelector } from 'react-redux'

import type { MenuPreferences } from '@library/api-menu-experience'
import {
  menuRecipeTileFetcher,
  transformMenuRecipeTileResponseToRecipe,
} from '@library/menu-service'

import { useMenuMeta } from 'hooks/menu-metadata/useMenuMeta'
import { getBasketDate } from 'selectors/basket'

import { RecipeList } from '../../../MenuRecipesPage/RecipeList'
import type {
  RefreshedRecipesById,
  RefreshRecipeTile,
} from '../../../MenuRecipesPage/RecipeList/RecipeList'
import { DetailOverlay } from '../../../components/Detail'

type RecipeGridProps = {
  menuPreferences?: MenuPreferences
}

export const RecipeGrid = ({ menuPreferences }: RecipeGridProps) => {
  const [shouldShowOverlay, setShouldShowOverlay] = useState(false)
  const { menuId } = useMenuMeta()
  const deliveryDate = useSelector(getBasketDate)
  // Temporary UI override state until basket supports recipe-level portions.
  const [refreshedRecipesById, setRefreshedRecipesById] = useState<RefreshedRecipesById>({})

  useEffect(() => {
    setShouldShowOverlay(true)
  }, [])

  useEffect(() => {
    setRefreshedRecipesById({})
  }, [deliveryDate, menuId])

  const refreshRecipeTile = useCallback<RefreshRecipeTile>(
    async (recipeId, numPortions) => {
      if (!menuId) {
        return
      }

      try {
        const recipeTile = await menuRecipeTileFetcher({
          menuId,
          recipeId,
          numPortions,
          ...(deliveryDate && { deliveryDate }),
        })
        const refreshedRecipe = transformMenuRecipeTileResponseToRecipe(recipeTile)

        setRefreshedRecipesById((currentRefreshedRecipesById) => ({
          ...currentRefreshedRecipesById,
          [recipeId]: refreshedRecipe,
        }))
      } catch {
        datadogRum.addAction('recipe_tile_refresh_failed', {
          menu_id: menuId,
          recipe_id: recipeId,
          num_portions: numPortions,
        })
        // Best effort only: failed refreshes leave the visible tile data unchanged.
      }
    },
    [deliveryDate, menuId],
  )

  return (
    <div data-testid="menuRecipesList">
      <RecipeList
        menuPreferences={menuPreferences}
        refreshedRecipesById={refreshedRecipesById}
        refreshRecipeTile={refreshRecipeTile}
      />
      <DetailOverlay showOverlay={shouldShowOverlay} />
    </div>
  )
}
